import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import PortalDropdown from '../../../components/ui/PortalDropdown';
import useTranslation from '../../../hooks/useTranslation';
import { supabase } from '../../../lib/supabase';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { formatCurrency as fmtCAD } from '../../../utils/formatCurrency';
import { statusVariant } from '../../../utils/statusVariant';
import Badge, { BADGE_VARIANTS } from '../../../components/ui/Badge';

const CONTRACT_STATUS_CONFIG = {
  to_assign:         { label: 'To Assign', bg: 'bg-gray-100',   text: 'text-gray-500',  dot: 'bg-gray-400'   },
  draft:             { label: 'Draft',     bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-400' },
  pending_signature: { label: 'Draft',     bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-400' },
  sent:              { label: 'Sent',      bg: 'bg-blue-100',   text: 'text-blue-700',  dot: 'bg-blue-500'   },
  signed:            { label: 'Signed',    bg: 'bg-green-100',  text: 'text-green-700', dot: 'bg-green-500'  },
  active:            { label: 'Active',    bg: 'bg-green-100',  text: 'text-green-700', dot: 'bg-green-500'  },
  completed:         { label: 'Completed', bg: 'bg-gray-100',   text: 'text-gray-600',  dot: 'bg-gray-500'   },
  cancelled:         { label: 'Cancelled', bg: 'bg-red-100',    text: 'text-red-600',   dot: 'bg-red-400'    },
};

// Statuses available in the dropdown (excludes legacy pending_signature — treated as draft)
// 'completed' is intentionally NOT offered here (redundant with 'signed'); existing
// rows already set to 'completed' still render via CONTRACT_STATUS_CONFIG above.
const CONTRACT_STATUS_OPTIONS = ['to_assign', 'draft', 'sent', 'signed', 'cancelled'];

// ─── StatusPill ────────────────────────────────────────────────────────────────
const StatusPill = ({ subcontract, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const ref = useRef(null);
  const { t } = useTranslation();
  const currentStatus = subcontract?.status || 'to_assign';
  const cfg = CONTRACT_STATUS_CONFIG?.[currentStatus] || CONTRACT_STATUS_CONFIG?.to_assign;

  /**
   * Un contrat sans sous-traitant est un POSTE BUDGETAIRE non octroye, pas
   * un brouillon en negociation. Le laisser passer a "signe" reviendrait a
   * declarer signe un contrat que personne n'a obtenu.
   *
   * Derive de la ligne, jamais d'un etat local : assigner un sous-traitant
   * deverrouille le menu au prochain rendu, sans action supplementaire.
   */
  const isUnassigned = !subcontract?.subcontractorId && !subcontract?.subcontractor?.id;
  const lockedStatus = isUnassigned ? 'to_assign' : currentStatus;

  // Use i18n labels for known new statuses, fall back to config label
  const getLabel = (status) => {
    const labelMap = {
      to_assign: t('contracts.statusOptions.toAssign') || 'To Assign',
      draft: t('contracts.statusOptions.draft') || 'Draft',
      // pending_signature est un statut A PART ENTIERE : le peindre en
      // "Brouillon" faisait passer deux etats distincts pour un seul.
      pending_signature: t('contracts.statusOptions.pendingSignature') || 'Pending signature',
      sent: t('contracts.statusOptions.sent') || 'Sent',
      signed: t('contracts.statusOptions.signed') || 'Signed',
    };
    return labelMap?.[status] || CONTRACT_STATUS_CONFIG?.[status]?.label || status;
  };

  const handleSelect = async (status) => {
    setOpen(false);
    // Le menu est deja neutralise ; on ne compte pas sur l'interface seule
    // pour proteger une regle metier.
    if (isUnassigned) return;
    if (status === currentStatus) return;
    setUpdating(true);
    try {
      await onUpdate?.(subcontract?.id, { status });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="relative">
      <Badge
        as="button"
        ref={ref}
        variant={statusVariant('contract', lockedStatus)}
        onClick={() => !updating && !isUnassigned && setOpen((v) => !v)}
        disabled={updating || isUnassigned}
        className="cursor-pointer select-none transition-opacity hover:opacity-80 disabled:opacity-60 disabled:cursor-not-allowed"
        title={isUnassigned ? t('contracts.statusLocked.tooltip') : 'Change status'}
      >
        {getLabel(lockedStatus)}
        {updating ? (
          <Icon name="Loader" size={10} className="animate-spin" />
        ) : isUnassigned ? (
          <Icon name="Lock" size={10} />
        ) : (
          <Icon name="ChevronDown" size={10} />
        )}
      </Badge>
      <PortalDropdown
        anchorRef={ref}
        open={open && !isUnassigned}
        onClose={() => setOpen(false)}
        align="left"
        width={150}
        items={CONTRACT_STATUS_OPTIONS?.map((status) => ({
          label: getLabel(status),
          dot: BADGE_VARIANTS?.[statusVariant('contract', status)]?.dot,
          active: currentStatus === status,
          onClick: () => handleSelect(status),
        }))}
      />
    </div>
  );
};

// ─── DocumentIconButton ────────────────────────────────────────────────────────
const DocumentIconButton = ({ subcontract, projectId, onClick }) => {
  const [docCount, setDocCount] = useState(0);
  const [docNames, setDocNames] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeout = useRef(null);

  useEffect(() => {
    if (!subcontract?.id || !projectId) return;
    const storagePath = `${projectId}/${subcontract?.id}`;
    supabase?.storage
      ?.from('contracts-documents')
      ?.list(storagePath, { limit: 20, sortBy: { column: 'name', order: 'asc' } })
      ?.then(({ data }) => {
        const filtered = (data || [])?.filter(f => f?.name && f?.name !== '.emptyFolderPlaceholder');
        setDocCount(filtered?.length);
        setDocNames(filtered?.map(f => f?.name));
      })
      ?.catch(() => {});
  }, [subcontract?.id, projectId]);

  const handleMouseEnter = () => {
    tooltipTimeout.current = setTimeout(() => setShowTooltip(true), 200);
  };

  const handleMouseLeave = () => {
    clearTimeout(tooltipTimeout?.current);
    setShowTooltip(false);
  };

  const hasDocuments = docCount > 0;

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        onClick={onClick}
        className="relative p-1 rounded transition-colors group"
        title="Documents"
      >
        <Icon
          name="FileText"
          size={13}
          className={`transition-colors ${hasDocuments ? 'text-orange-500' : 'text-gray-400'} group-hover:text-gray-700`}
        />
        {/* Badge */}
        <span
          className={`absolute -top-1 -right-1 text-[10px] rounded-full px-1 leading-tight font-medium ${
            hasDocuments
              ? 'bg-orange-100 text-orange-600' :'bg-gray-200 text-gray-400'
          }`}
          style={{ minWidth: '14px', textAlign: 'center' }}
        >
          {docCount}
        </span>
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white shadow-md rounded-md px-2 py-1.5 min-w-[120px] max-w-[180px] pointer-events-none">
          {hasDocuments ? (
            <div className="space-y-0.5">
              {docNames?.slice(0, 3)?.map((name, i) => (
                <p key={i} className="text-[10px] text-gray-600 truncate">{name}</p>
              ))}
              {docCount > 3 && (
                <p className="text-[10px] text-gray-400">+{docCount - 3} more</p>
              )}
            </div>
          ) : (
            <p className="text-[10px] text-gray-500">No documents</p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── ActionsMenu (3-dot) ───────────────────────────────────────────────────────
const ActionsMenu = ({ subcontract, onEdit, onDelete, onViewDocuments }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const { t } = useTranslation();

  const items = [
    {
      label: t('contracts.actions.edit'),
      icon: 'Pencil',
      onClick: () => onEdit?.(subcontract),
    },
    {
      label: t('contracts.actions.delete'),
      icon: 'Trash2',
      onClick: () => onDelete?.(subcontract?.id),
      variant: 'danger',
    },
  ];

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        title="Actions"
      >
        <Icon name="MoreVertical" size={15} />
      </button>
      <PortalDropdown
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        width={180}
      />
    </div>
  );
};

// ─── DocumentsModal ────────────────────────────────────────────────────────────
const DocumentsModal = ({ subcontract, projectId, onClose }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [deleteConfirmFile, setDeleteConfirmFile] = useState(null);
  const fileInputRef = useRef(null);

  const storagePath = `${projectId}/${subcontract?.id}`;

  // ── File type icon helper ──────────────────────────────────────────────────
  const getFileTypeIcon = (fileName) => {
    const ext = fileName?.split('.')?.pop()?.toLowerCase();
    if (ext === 'pdf') {
      return <Icon name="FileText" size={15} className="text-red-500 flex-shrink-0" />;
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']?.includes(ext)) {
      return <Icon name="Image" size={15} className="text-purple-500 flex-shrink-0" />;
    }
    if (['xls', 'xlsx', 'csv']?.includes(ext)) {
      return <Icon name="FileSpreadsheet" size={15} className="text-green-600 flex-shrink-0" />;
    }
    if (['doc', 'docx']?.includes(ext)) {
      return <Icon name="FileText" size={15} className="text-blue-500 flex-shrink-0" />;
    }
    return <Icon name="File" size={15} className="text-gray-400 flex-shrink-0" />;
  };

  const fetchFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const { data, error } = await supabase?.storage
        ?.from('contracts-documents')
        ?.list(storagePath, { limit: 100, sortBy: { column: 'name', order: 'asc' } });
      if (error) {
        console.error('Error listing files:', error);
        setFiles([]);
      } else {
        setFiles((data || [])?.filter(f => f?.name && f?.name !== '.emptyFolderPlaceholder'));
      }
    } catch (err) {
      console.error('Unexpected error listing files:', err);
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [storagePath]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const sanitizeFilename = (name) => {
    return name?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '')?.toLowerCase()?.replace(/\s+/g, '_')?.replace(/[^a-z0-9_.]/g, '')?.replace(/_+/g, '_');
  };

  const uploadFiles = async (fileList) => {
    if (!fileList?.length) return;
    setUploadError(null);
    setUploading(true);

    const MAX_SIZE = 20 * 1024 * 1024; // 20MB (bucket limit)
    const errors = [];

    for (const file of Array.from(fileList)) {
      if (file?.size > MAX_SIZE) {
        errors?.push(`${file?.name}: File too large (max 20MB)`);
        continue;
      }
      const sanitizedName = sanitizeFilename(file?.name);
      const filePath = `${storagePath}/${sanitizedName}`;
      try {
        const { error: uploadErr } = await supabase?.storage
          ?.from('contracts-documents')
          ?.upload(filePath, file, { upsert: true, cacheControl: '3600' });
        if (uploadErr) {
          errors?.push(`${file?.name}: ${uploadErr?.message}`);
        }
      } catch (err) {
        errors?.push(`${file?.name}: Unexpected error`);
      }
    }

    if (errors?.length) {
      setUploadError(errors?.join('\n'));
    }
    setUploading(false);
    await fetchFiles();
  };

  const handleFileInputChange = (e) => {
    uploadFiles(e?.target?.files);
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    uploadFiles(e?.dataTransfer?.files);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragEnter = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const deleteFile = async (fileName) => {
    setDeletingFile(fileName);
    const filePath = `${storagePath}/${fileName}`;
    try {
      const { error } = await supabase?.storage
        ?.from('contracts-documents')
        ?.remove([filePath]);
      if (error) {
        alert('Could not delete file: ' + error?.message);
      } else {
        setFiles((prev) => prev?.filter((f) => f?.name !== fileName));
      }
    } catch (err) {
      alert('Unexpected error deleting file.');
    } finally {
      setDeletingFile(null);
    }
  };

  const openFile = async (fileName) => {
    const filePath = `${storagePath}/${fileName}`;
    try {
      const { data, error } = await supabase?.storage
        ?.from('contracts-documents')
        ?.createSignedUrl(filePath, 60 * 60);
      if (error) {
        alert('Could not open file: ' + error?.message);
        return;
      }
      if (data?.signedUrl) {
        window.open(data?.signedUrl, '_blank');
      }
    } catch (err) {
      alert('Unexpected error opening file.');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const subName = subcontract?.subcontractor?.companyName || 'N/A';
  const costCode = subcontract?.projectCost?.costCode || '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Icon name="FolderOpen" size={16} className="text-primary" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Documents</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {subName}{costCode ? ` · ${costCode}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* File List */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loadingFiles ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
            </div>
          ) : files?.length === 0 ? (
            <div className="text-center py-6">
              <Icon name="FileX" size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-xs text-gray-400">No documents attached yet</p>
            </div>
          ) : (
            <ul className="space-y-1">
              {files?.map((file) => (
                <li key={file?.name} className="group relative">
                  <button
                    onClick={() => openFile(file?.name)}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
                  >
                    {getFileTypeIcon(file?.name)}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate group-hover:text-primary transition-colors">
                        {file?.name}
                      </p>
                      {file?.metadata?.size && (
                        <p className="text-[10px] text-gray-400">{formatFileSize(file?.metadata?.size)}</p>
                      )}
                    </div>
                  </button>
                  {/* Delete button — revealed on row hover */}
                  <button
                    onClick={(e) => { e?.stopPropagation(); setDeleteConfirmFile(file?.name); }}
                    disabled={deletingFile === file?.name}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                    title="Delete file"
                  >
                    {deletingFile === file?.name
                      ? <Icon name="Loader2" size={13} className="animate-spin text-red-400" />
                      : <Icon name="Trash2" size={13} />
                    }
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Upload Zone */}
        <div className="px-5 pb-5 flex-shrink-0">
          {uploadError && (
            <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 whitespace-pre-line">
              {uploadError}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileInputChange}
          />
          <div
            onClick={() => !uploading && fileInputRef?.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-xl px-4 py-6 text-center cursor-pointer transition-all ${
              isDragOver
                ? 'border-primary bg-orange-50 scale-[1.01]'
                : 'border-gray-200 hover:border-primary hover:bg-orange-50/30'
            } ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Icon name="Loader2" size={20} className="animate-spin text-primary" />
                <p className="text-xs text-gray-500">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Icon name="Upload" size={20} className={isDragOver ? 'text-primary' : 'text-gray-400'} />
                <p className="text-xs font-medium text-gray-600">
                  Drop files here or <span className="text-primary">click to upload</span>
                </p>
                <p className="text-[10px] text-gray-400">PDF, Excel, Word supported · Max 20MB each</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Delete Confirmation Modal ── */}
      <ConfirmDialog
        isOpen={!!deleteConfirmFile}
        confirmVariant="danger"
        title={t('documents.delete.title')}
        message={t('documents.delete.message')}
        confirmLabel={t('documents.delete.confirm')}
        cancelLabel={t('documents.delete.cancel')}
        onConfirm={async () => {
          const name = deleteConfirmFile;
          setDeleteConfirmFile(null);
          await deleteFile(name);
        }}
        onCancel={() => setDeleteConfirmFile(null)}
        onClose={() => setDeleteConfirmFile(null)}
      >
        <p className="text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 truncate">
          {deleteConfirmFile}
        </p>
      </ConfirmDialog>
    </div>
  );
};

// ─── SubcontractsTable ─────────────────────────────────────────────────────────
const SubcontractsTable = ({ subcontracts, onUpdate, onDelete, onNoteUpdate, onEdit, sortConfig: sortConfigProp, onSort, revisedContractMap, coCountMap, coNumberMap, projectId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [localSortConfig, setLocalSortConfig] = useState({ key: null, direction: 'asc' });

  // Note modal state
  const [noteModalContract, setNoteModalContract] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  // Documents modal state
  const [documentsContract, setDocumentsContract] = useState(null);

  // Use prop-driven sort when provided, otherwise fall back to local
  const sortConfig = sortConfigProp !== undefined ? sortConfigProp : localSortConfig;

  const handleSort = (key) => {
    if (onSort) {
      onSort(key);
    } else {
      let direction = 'asc';
      if (localSortConfig?.key === key && localSortConfig?.direction === 'asc') {
        direction = 'desc';
      }
      setLocalSortConfig({ key, direction });
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={12} className="text-gray-400" />;
    }
    return sortConfig?.direction === 'asc' ? (
      <Icon name="ChevronUp" size={12} className="text-primary" />
    ) : (
      <Icon name="ChevronDown" size={12} className="text-primary" />
    );
  };

  const sortedSubcontracts = sortConfigProp !== undefined
    ? [...(subcontracts || [])]
    : [...(subcontracts || [])]?.sort((a, b) => {
        if (!localSortConfig?.key) return 0;
        let aValue, bValue;
        switch (localSortConfig?.key) {
          case 'cost_code':
            aValue = a?.projectCost?.costCode?.toLowerCase() || '';
            bValue = b?.projectCost?.costCode?.toLowerCase() || '';
            break;
          case 'subcontractor':
            aValue = a?.subcontractor?.companyName?.toLowerCase() || '';
            bValue = b?.subcontractor?.companyName?.toLowerCase() || '';
            break;
          case 'trade':
            aValue = a?.subcontractor?.trade?.toLowerCase() || '';
            bValue = b?.subcontractor?.trade?.toLowerCase() || '';
            break;
          case 'contract_value':
            aValue = parseFloat(a?.contractValue) || 0;
            bValue = parseFloat(b?.contractValue) || 0;
            break;
          case 'status':
            aValue = a?.status || '';
            bValue = b?.status || '';
            break;
          default:
            return 0;
        }
        if (aValue < bValue) return localSortConfig?.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return localSortConfig?.direction === 'asc' ? 1 : -1;
        return 0;
      });

  // Note modal handlers
  const openNoteModal = (subcontract) => {
    setNoteModalContract(subcontract);
    setNoteText(subcontract?.notes || '');
  };

  const closeNoteModal = () => {
    setNoteModalContract(null);
    setNoteText('');
    setNoteSaving(false);
  };

  const handleNoteSave = async () => {
    if (!noteModalContract || !onNoteUpdate) return;
    setNoteSaving(true);
    const success = await onNoteUpdate(noteModalContract?.id, noteText);
    if (success) {
      closeNoteModal();
    } else {
      setNoteSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return fmtCAD(value);
  };

  return (
    <>
      <div>
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="border-b border-gray-200">
              <th
                className="text-left text-xs font-medium text-gray-500 px-4 py-3 cursor-pointer select-none hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('cost_code')}
              >
                <div className="flex items-center gap-1">
                  <span>{t('contracts.costCode')}</span>
                  <SortIcon columnKey="cost_code" />
                </div>
              </th>
              <th
                className="text-left text-xs font-medium text-gray-500 px-4 py-3 cursor-pointer select-none hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('subcontractor')}
              >
                <div className="flex items-center gap-1">
                  <span>{t('contracts.subcontractor')}</span>
                  <SortIcon columnKey="subcontractor" />
                </div>
              </th>
              <th
                className="text-right text-xs font-medium text-gray-500 px-4 py-3 cursor-pointer select-none hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('contract_value')}
              >
                <div className="flex items-center justify-end gap-1">
                  <span>{t('contracts.contractValue')}</span>
                  <SortIcon columnKey="contract_value" />
                </div>
              </th>
              {revisedContractMap && (
                <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <span>{t('contracts.table.revisedContract')}</span>
                  </div>
                </th>
              )}
              <th
                className="text-left text-xs font-medium text-gray-500 px-4 py-3 cursor-pointer select-none hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-1">
                  <span>{t('contracts.table.status')}</span>
                  <SortIcon columnKey="status" />
                </div>
              </th>
              <th className="text-left text-xs font-medium text-gray-500 px-4 py-3 w-[80px]">
                {t('contracts.table.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedSubcontracts?.length === 0 ? (
              <tr>
                <td colSpan={revisedContractMap ? 6 : 5} className="px-4 py-8 text-center text-xs text-gray-500">
                  {t('contracts.table.noContracts')}
                </td>
              </tr>
            ) : (
              sortedSubcontracts?.map((subcontract) => {
                const hasNote = !!(subcontract?.notes?.trim());

                return (
                  <tr key={subcontract?.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    {/* Cost Code */}
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-xs font-medium text-gray-900 leading-tight">
                          {subcontract?.projectCost?.costCode || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500 leading-tight">
                          {subcontract?.projectCost?.description || ''}
                        </p>
                      </div>
                    </td>
                    {/* Subcontractor */}
                    <td className="px-4 py-3">
                      {subcontract?.status === 'to_assign' ? (
                        <span className="text-xs text-gray-400 italic">—</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary font-semibold text-[10px]">
                            {subcontract?.subcontractor?.companyName?.split(' ')?.map(n => n?.[0])?.join('') || 'SC'}
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-900 leading-tight">
                              {subcontract?.subcontractor?.companyName || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500 leading-tight">
                              {subcontract?.subcontractor?.contactPerson || ''}
                            </p>
                          </div>
                        </div>
                      )}
                    </td>
                    {/* Contract Value */}
                    <td className="px-4 py-3 text-right">
                      {subcontract?.status === 'to_assign' ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : subcontract?.pricingModel === 'labor_material' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700 whitespace-nowrap">
                          {t('contracts.pricingModelOptions.laborMaterial')}
                        </span>
                      ) : (
                        <span className="text-xs font-semibold text-gray-900 whitespace-nowrap">
                          {formatCurrency(subcontract?.contractValue)}
                        </span>
                      )}
                    </td>
                    {/* Revised Contract */}
                    {revisedContractMap && (
                      <td className="px-4 py-3 text-right">
                        {subcontract?.status === 'to_assign' ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : subcontract?.pricingModel === 'labor_material' ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : (
                          <div className="flex flex-col items-end">
                            <span className="text-xs font-semibold text-blue-700 whitespace-nowrap">
                              {formatCurrency(revisedContractMap?.[subcontract?.id] ?? 0)}
                            </span>
                          </div>
                        )}
                      </td>
                    )}
                    {/* Status */}
                    <td className="px-4 py-3">
                      <StatusPill subcontract={subcontract} onUpdate={onUpdate} />
                    </td>
                    {/* Actions — document icon + 3-dot menu */}
                    <td className="px-4 py-3">
                      <div className="flex items-end justify-end gap-2 h-full">
                        <div className="relative flex items-end">
                          <DocumentIconButton
                            subcontract={subcontract}
                            projectId={projectId}
                            onClick={() => setDocumentsContract(subcontract)}
                          />
                        </div>
                        <div className="flex items-end">
                          <ActionsMenu
                            subcontract={subcontract}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onViewDocuments={(sc) => setDocumentsContract(sc)}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Note Modal */}
      {noteModalContract && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeNoteModal} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icon name="FileText" size={16} className="text-primary" />
                <h3 className="text-sm font-semibold text-gray-900">
                  {t('contracts.noteModal.title')}
                </h3>
              </div>
              <button
                onClick={closeNoteModal}
                className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Icon name="X" size={15} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              {noteModalContract?.subcontractor?.companyName || 'N/A'}
              {noteModalContract?.projectCost?.costCode ? ` · ${noteModalContract?.projectCost?.costCode}` : ''}
            </p>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e?.target?.value)}
              placeholder={t('contracts.noteModal.placeholder')}
              rows={4}
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none transition-colors"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={closeNoteModal}
                disabled={noteSaving}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleNoteSave}
                disabled={noteSaving}
                className="px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {noteSaving ? (
                  <>
                    <Icon name="Loader2" size={12} className="animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('common.save')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Documents Modal */}
      {documentsContract && (
        <DocumentsModal
          subcontract={documentsContract}
          projectId={projectId}
          onClose={() => setDocumentsContract(null)}
        />
      )}
    </>
  );
};

export default SubcontractsTable;